import { useRef, useState, useLayoutEffect, useEffect, useMemo, useCallback } from "react";
import { ScrollView } from "react-native";
import { useNavigation, useLocalSearchParams } from "expo-router";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import SegmentedControl from "@src/components/SegmentedControl";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { DataStateView } from "@src/components/screen-state";
import { EmailContactList } from "../components/EmailContactList";
import { SmsContactList } from "../components/SmsContactList";
import { ContactTab, ContactCategory, EmailContact, SmsContact } from "../types";
import { useCameraContacts, useUpdateCameraContacts, useCameraEmailSuggestions } from "@src/services/api/hooks";
import { Icon } from "@src/components/Icon";
import { FormInput } from "@src/components/FormInput";
import { EmailSuggestionsList } from "../components/EmailSuggestionsList";
import { PressableScale } from "pressto";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { BottomSheetModal } from "@gorhom/bottom-sheet";
import { FloatingSelectionBar } from "@src/features/settings/components/FloatingSelectionBar";
import { CreateContactBottomSheet, CreateContactData } from "../components/CreateContactBottomSheet";
import { useContactsStore, splitToApiContacts, UnifiedContact } from "../stores/contacts-store";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";

type ContactsScreenProps = {
  title?: string;
  category: ContactCategory;
};

export function ContactsScreen({ title = "Contacts", category }: ContactsScreenProps) {
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const { siteId, cameraKey } = useLocalSearchParams<{
    siteId?: string;
    cameraKey?: string;
  }>();
  const [selectedTab, setSelectedTab] = useState<ContactTab>("email");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const { bottom } = useSafeAreaInsets();
  const createContactRef = useRef<BottomSheetModal>(null);
  const { headerLeft } = useHeaderBackButton();
  const hasCameraContext = Boolean(siteId && cameraKey);

  // Camera-scoped contacts (API-backed)
  const { data: cameraContacts, isLoading, isFetching, error, refetch } = useCameraContacts(siteId, cameraKey);
  const updateCameraContactsMutation = useUpdateCameraContacts();

  // Sync API data into unified contacts store
  const syncFromApi = useContactsStore((s) => s.syncFromApi);
  const storeKey = siteId && cameraKey ? `${siteId}:${cameraKey}` : "";
  const storeContacts = useContactsStore((s) => s.contactsByCamera[storeKey]);
  const unifiedContacts = useMemo(() => storeContacts ?? [], [storeContacts]);

  useEffect(() => {
    if (cameraContacts && siteId && cameraKey) {
      syncFromApi(siteId, cameraKey, cameraContacts);
    }
  }, [cameraContacts, siteId, cameraKey, syncFromApi]);

  // Email suggestions for adding new contacts
  const debouncedQuery = searchQuery.trim();
  const { data: emailSuggestions } = useCameraEmailSuggestions(
    siteId,
    cameraKey,
    debouncedQuery || undefined,
    selectedTab === "email",
  );

  const handleDeleteContact = (id: string) => {
    if (!hasCameraContext || !siteId || !cameraKey) return;

    const remaining = unifiedContacts.filter((c) => c.id !== id);
    const nextContacts = splitToApiContacts(remaining);

    updateCameraContactsMutation.mutate({
      siteId,
      cameraKey,
      contacts: nextContacts,
    });
  };

  const handleToggleEmail = (email: string, checked: boolean) => {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(email);
      } else {
        next.delete(email);
      }
      return next;
    });
  };

  const handleAddSelectedEmails = () => {
    if (!hasCameraContext || !siteId || !cameraKey || selectedEmails.size === 0) return;

    const newUnified: UnifiedContact[] = Array.from(selectedEmails).map((email) => ({
      id: `${email.toLowerCase()}|${email.toLowerCase()}|`,
      name: email,
      email,
    }));

    const nextContacts = splitToApiContacts([...unifiedContacts, ...newUnified]);
    updateCameraContactsMutation.mutate({ siteId, cameraKey, contacts: nextContacts });
    setSelectedEmails(new Set());
    setSearchQuery("");
  };

  const handleCreateContact = useCallback(
    (data: CreateContactData) => {
      if (!hasCameraContext || !siteId || !cameraKey) return;

      const fullName = `${data.firstName} ${data.lastName}`.trim();
      const newContact: UnifiedContact = {
        id: `${fullName.toLowerCase()}|${data.email?.toLowerCase() ?? ""}|${data.phoneNumber ?? ""}`,
        name: fullName,
        email: data.email,
        phoneNumber: data.phoneNumber,
      };

      const nextContacts = splitToApiContacts([...unifiedContacts, newContact]);

      updateCameraContactsMutation.mutate(
        { siteId, cameraKey, contacts: nextContacts },
        { onSuccess: () => createContactRef.current?.dismiss() },
      );
    },
    [hasCameraContext, siteId, cameraKey, unifiedContacts, updateCameraContactsMutation],
  );

  // Configure navigation header
  useLayoutEffect(() => {
    navigation.setOptions({
      title,
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
      headerTitle: () => (
        <View pr="5xl">
          <Text textStyle="md/semibold" numberOfLines={2} textAlign="center">
            {title}
          </Text>
        </View>
      ),
    });
  }, [navigation, title, headerLeft]);

  const renderContactsContent = () => {
    const emailContacts: EmailContact[] = unifiedContacts
      .filter((c) => c.email)
      .map((c) => ({ id: c.id, email: c.email!, category }));

    const smsContacts: SmsContact[] = unifiedContacts
      .filter((c) => c.phoneNumber)
      .map((c) => ({ id: c.id, name: c.name, phone_number: c.phoneNumber!, category }));

    return (
      <View flex={1} bg="background">
        <View px="5xl" py="4xl">
          <SegmentedControl
            optionsLabels={[
              { title: "Email", icon: "mail" },
              { title: "SMS", icon: "messageBubbleOutline" },
            ]}
            selectedOption={selectedTab === "email" ? "Email" : "SMS"}
            onSelectOption={(option) => setSelectedTab(option === "Email" ? "email" : "sms")}
          />
        </View>

        <ScrollView contentContainerStyle={{ flexGrow: 1, paddingHorizontal: theme.spacing.xl }}>
          {selectedTab === "email" ?
            <EmailContactList contacts={emailContacts} onDeleteContact={handleDeleteContact} />
          : <SmsContactList contacts={smsContacts} onDeleteContact={handleDeleteContact} />}
          <View py="7xl">
            {selectedTab === "email" ?
              <>
                <View mb="3xl" gap="2xl">
                  <Text textStyle="xs/regular" color="text">
                    Add new contact
                  </Text>
                  <FormInput
                    placeholder="Enter name or email"
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    iconLeft={<Icon name="search" color={theme.colors.textMuted} />}
                    clearable
                    size="medium"
                    containerStyle={styles.formInput}
                  />
                </View>
                {emailSuggestions ?
                  <EmailSuggestionsList
                    suggestions={emailSuggestions}
                    existingContacts={emailContacts}
                    searchQuery={searchQuery}
                    selectedEmails={selectedEmails}
                    onToggleEmail={handleToggleEmail}
                    onCreateContact={() => createContactRef.current?.present()}
                  />
                : null}
              </>
            : <PressableScale onPress={() => createContactRef.current?.present()}>
                <View style={styles.button}>
                  <Text textStyle="sm/regular">Create new contact</Text>
                  <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
                </View>
              </PressableScale>
            }
          </View>
        </ScrollView>
        <FloatingSelectionBar
          selectedCount={selectedEmails.size}
          actionLabel="Add contacts"
          onAction={handleAddSelectedEmails}
          bottomInset={bottom}
        />
      </View>
    );
  };

  return (
    <>
      <DataStateView
        data={cameraContacts}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error as Error | null}
        refetch={refetch}
        // isEmpty={() => unifiedContacts.length === 0}
        loadingMessage="Loading contacts..."
        errorTitle="Unable to load contacts"
        errorMessage="Please check your connection and try again."
        emptyState={{
          icon: "users",
          title: "No contacts yet",
          message: "Tap the + button to add an email or SMS contact.",
        }}
      >
        {() => renderContactsContent()}
      </DataStateView>
      <CreateContactBottomSheet
        bottomSheetRef={createContactRef}
        mode={selectedTab}
        onSave={handleCreateContact}
        isSaving={updateCameraContactsMutation.isPending}
      />
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  formInput: {
    backgroundColor: theme.colors.surfaceCard,
    borderColor: theme.colors.field.border_default,
  },
  button: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
  },
}));
